/* eslint-disable jsdoc/require-returns */
/* eslint-disable jsdoc/require-param */
import type { CartReturn } from '@shopify/hydrogen';
import { flattenConnection } from '@shopify/hydrogen';
import { useCartLayoutData } from '~/hooks/useCartLayoutData';
import type { UseProductMaxQtyProps } from './useProductQty.types';

/**
 * Get the quantity of a product from the cart. This combines quantities when a
 * product has been split into multiple lines due to different approval
 * documents.
 */
const getCartLineItemCombinedQuantity = ({
  cart,
  productId,
}: {
  productId?: string;
  cart?: CartReturn | null;
}): number => {
  const lineItemsQty = cart?.lines?.nodes.reduce((accumByQty, line) => {
    if (line.merchandise.product.id === productId) {
      return accumByQty + line.quantity;
    }

    return accumByQty;
  }, 0);

  return lineItemsQty ?? 0;
};

/**
 * Some products are allowed to be sold even when they are out of stock. For such products,
 * the logic should not consider the stock.
 */
export const allowBackorder = (availableForSale: boolean, quantityAvailable: number) => {
  return availableForSale && quantityAvailable <= 0;
};

/**
 * Get the min/max quantity for a product based on the stock level and cart
 * quantity.
 */
export const useProductQty = ({ product, qtyMaxLimit, qtyMinLimit, qtyToAdd }: UseProductMaxQtyProps) => {
  // Get the product quantity from the cart
  const { cart } = useCartLayoutData();
  const qtyInCart = getCartLineItemCombinedQuantity({ productId: product?.id, cart });
  const productVariant = product ? flattenConnection(product?.variants)[0] : undefined;

  /**
   * Quantity available in stock. Defaults to 0 if the product is loading or
   * unavailable.
   */
  const qtyInStock = productVariant?.quantityAvailable ?? 0;
  const isBackorderAllowed = allowBackorder(!!productVariant?.availableForSale, qtyInStock);

  /**
   * The maximum quantity that can be ordered.
   * If the product allows backorders, it uses the maximum order limit.
   * Otherwise, it is the lesser of the maximum order limit and the available stock.
   */
  const qtyMaxToOrder = isBackorderAllowed ? qtyMaxLimit : Math.min(qtyMaxLimit, qtyInStock);

  /**
   * The maximum quantity that can be added to the cart without
   * exceeding the maximum limit.
   */
  const qtyMaxToAdd = Math.max(qtyMaxToOrder - qtyInCart, 0);

  /**
   * Check if quantity is below minimum order.
   * Minimum only applies on first add (when cart is empty).
   * Once items are in cart, user can add any amount from 1 up to max.
   */
  const isBelowMin = qtyToAdd === '' ? false : qtyInCart === 0 ? qtyToAdd < qtyMinLimit : qtyToAdd < 1;

  // Check if quantity is above maximum stock
  const isAboveMaxLimit = qtyToAdd === '' ? false : qtyInCart + qtyToAdd > qtyMaxLimit;
  const isAboveMax = isAboveMaxLimit || (qtyToAdd === '' ? false : qtyToAdd > qtyMaxToAdd);

  /**
   * Check if the minimum quantity exceeds the maximum quantity. This ensures
   * that left over stock can still be added to the cart when there is an amount
   * remaining.
   */
  const isRemainingStock = qtyMaxToAdd < qtyMinLimit && isBelowMin;

  return {
    isAboveMax,
    isBelowMin,
    isRemainingStock,
    qtyInCart,
    qtyInStock,
    qtyMaxToAdd,
    qtyMaxToOrder,
  };
};
